/**
 * useDisplaySettings — Global display preferences stored in localStorage.
 *
 * Controls: sources visibility, detailed stats, compact mode, reduced motion.
 * Triggers re-renders via useSyncExternalStore for cross-component sync.
 *
 * @since Display Settings v1
 */

import { useSyncExternalStore, useCallback } from "react";

// ── Keys ──────────────────────────────────────────
const KEYS = {
  showSources: "perform_show_sources",
  showDetailedStats: "perform_show_detailed_stats",
  compactMode: "perform_compact_mode",
  reducedMotion: "perform_reduced_motion",
} as const;

// ── Defaults ──────────────────────────────────────
const DEFAULTS: Record<keyof typeof KEYS, boolean> = {
  showSources: true,
  showDetailedStats: true,
  compactMode: false,
  reducedMotion: false,
};

// ── External store for cross-component sync ───────
let listeners: Set<() => void> = new Set();
function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function emitChange() {
  listeners.forEach((cb) => cb());
}

function readBool(key: string, defaultVal: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return defaultVal;
    return v === "true";
  } catch {
    return defaultVal;
  }
}

function writeBool(key: string, val: boolean) {
  try {
    localStorage.setItem(key, String(val));
  } catch {}
  emitChange();
}

function getSnapshot() {
  return JSON.stringify({
    showSources: readBool(KEYS.showSources, DEFAULTS.showSources),
    showDetailedStats: readBool(KEYS.showDetailedStats, DEFAULTS.showDetailedStats),
    compactMode: readBool(KEYS.compactMode, DEFAULTS.compactMode),
    reducedMotion: readBool(KEYS.reducedMotion, DEFAULTS.reducedMotion),
  });
}

export interface DisplaySettings {
  showSources: boolean;
  showDetailedStats: boolean;
  compactMode: boolean;
  reducedMotion: boolean;
  setShowSources: (v: boolean) => void;
  setShowDetailedStats: (v: boolean) => void;
  setCompactMode: (v: boolean) => void;
  setReducedMotion: (v: boolean) => void;
}

export function useDisplaySettings(): DisplaySettings {
  const raw = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const parsed = JSON.parse(raw);

  return {
    showSources: parsed.showSources,
    showDetailedStats: parsed.showDetailedStats,
    compactMode: parsed.compactMode,
    reducedMotion: parsed.reducedMotion,
    setShowSources: useCallback((v: boolean) => writeBool(KEYS.showSources, v), []),
    setShowDetailedStats: useCallback((v: boolean) => writeBool(KEYS.showDetailedStats, v), []),
    setCompactMode: useCallback((v: boolean) => writeBool(KEYS.compactMode, v), []),
    setReducedMotion: useCallback((v: boolean) => writeBool(KEYS.reducedMotion, v), []),
  };
}
